import { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
// 生成キューのみを購読する (Context 4分割後の個別フック)。設定・素材・属性ツリーの
// 更新ではこのウィンドウは再レンダーしない。
import { useGenerationJobs } from './state/jobsContext';
// シナリオ生成の「切り離し」。巨大な useScenarioGeneration には依存せず、
// track レジストリの detachTrack だけを使う (計画書 §4.1)。
import { detachTrack } from './scenario/scenarioGenerationTracks';
import type { ActiveJob, VideoEditRenderQueueResult } from './state/types';
import { MaterialPreviewModal } from '../materials/shared/MaterialPreviewModal';
import { apiClient } from '../../services/apiClient';
import { fetchMaterialContentBlob } from '../../features/materials/api';
import type { TemporaryMaterial } from '../../features/materials/materialsSlice';
import { previewFilename } from '../../utils/filename';
import { NotifyToggleSwitch } from '../notifications/NotifyToggleSwitch';
import { fetchVideoEtaTable, estimateVideoSec, type VideoEtaTable } from '../../services/videoEta';
import { brandConfig } from '../../config/brands';
import { MotionCandidateStrip } from './MotionCandidateStrip';
import { motionStopErrorMessage, motionStopStageText, stopMotionJob, type MotionStop } from '../../features/motion/jobs';
import { useToast } from '../../hooks/useToast';
import styles from './GenerationWorkspace.module.css';

async function fetchTemporaryMaterial(materialId: string): Promise<TemporaryMaterial> {
  const { data } = await apiClient.get<TemporaryMaterial>(`/materials/temporary/${materialId}`);
  return data;
}

function formatElapsed(startTime: number, now: number): string {
  const seconds = Math.floor((now - startTime) / 1000);
  if (seconds < 60) return `${seconds}秒前`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}分前`;
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

/** Blob を <a download> で保存する。画面遷移は起きない。 */
function downloadBlob(blob: Blob, filename: string) {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
}

/**
 * MP4 書き出し (video-edit-render) の完了項目に出す情報。useExportJob が result に入れる
 * (VideoEditRenderQueueResult)。キュー側だけで完了を追った場合 (useEditorJobQueue.watch) は
 * material_id しか無いので null (項目クリックのプレビューだけになる)。
 */
function videoEditRenderResultOf(aj: ActiveJob): VideoEditRenderQueueResult | null {
  if (aj.type !== 'video-edit-render' || aj.status !== 'completed') return null;
  const r = aj.result as Partial<VideoEditRenderQueueResult> | undefined;
  if (!r || typeof r.material_id !== 'string' || typeof r.filename !== 'string' || typeof r.width !== 'number') return null;
  return r as VideoEditRenderQueueResult;
}

// ジョブ種別ごとの想定所要時間(秒)。時間ベースの疑似進捗を出すための目安であり、
// 実際の進捗(progress 0-100)ではない。外部プロバイダ(動画生成等)は細粒度の
// 進捗を返さないため、経過時間と想定尺から体感用のバーを描画する。
// 動画 (video) は実測ベースの ETA テーブルで上書きする (resolveExpectedSec)。
const EXPECTED_DURATION_SEC: Record<string, number> = {
  image: 25,
  video: 120,
  'scene-preview': 25,
  'concat-video': 60,
  storyboard: 120,
  scenario: 25,
  'video-edit-propose': 90,
  'video-edit-analyze': 60,
  'video-edit-render': 120,
};
const DEFAULT_EXPECTED_SEC = 30;

// 動画エディタのジョブの文言 (実行中 / 完了 / 失敗)。QueueItem の種別ごとの三項演算子の連鎖に足す代わりに表で持つ。
const VIDEO_EDIT_JOB_TEXTS: Record<string, { running: string; completed: string; failed: string }> = {
  'video-edit-propose': { running: 'AI編集を提案中...', completed: 'AI編集の提案完了', failed: 'AI編集の提案エラー' },
  'video-edit-analyze': { running: 'シーンを解析中...', completed: 'シーンの解析完了', failed: 'シーンの解析エラー' },
  'video-edit-render': { running: 'MP4を書き出し中...', completed: 'MP4書き出し完了', failed: 'MP4書き出しエラー' },
};

// ジョブの想定所要時間(秒)を解決する。動画でモデルが判れば実測 ETA を優先し、
// それ以外 / 未取得時は種別ごとの固定目安にフォールバックする。
function resolveExpectedSec(aj: ActiveJob, etaTable: VideoEtaTable | null | undefined): number {
  if (aj.type === 'video' && aj.videoModel) {
    const est = estimateVideoSec(etaTable, aj.videoModel, aj.durationSeconds);
    if (est != null) return est;
  }
  return EXPECTED_DURATION_SEC[aj.type ?? ''] ?? DEFAULT_EXPECTED_SEC;
}

// 経過時間から疑似進捗(0-99)を算出。想定尺までは線形で90%まで進み、
// 超過後は90→99%へ漸近させて「終わりそうで終わらない」感を出す(完了で100%)。
function pseudoProgress(elapsedMs: number, expectedSec: number): number {
  const expected = expectedSec * 1000;
  if (elapsedMs <= 0) return 0;
  if (elapsedMs < expected) {
    return (elapsedMs / expected) * 90;
  }
  const overrun = elapsedMs - expected;
  return 90 + (1 - Math.exp(-overrun / expected)) * 9;
}

export function GenerationQueueWindow() {
  const { activeJobs, setActiveJobs, showJobQueueModal, setShowJobQueueModal } = useGenerationJobs();
  const [queueWindowPos, setQueueWindowPos] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const queueWindowRef = useRef<HTMLDivElement>(null);
  const [materialModalId, setMaterialModalId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const prevJobCountRef = useRef(activeJobs.length);
  const queryClient = useQueryClient();
  // 疑似進捗の更新用に1秒ごとに進むタイマー値
  const [now, setNow] = useState(() => Date.now());
  // 各ジョブが processing(=waiting false)へ遷移した時刻。進捗バーの基準に使う
  // (順番待ち時間を進捗に含めないため startedAt ではなくこちらを基準にする)。
  const processingStartRef = useRef<Record<string, number>>({});

  // processing 開始時刻を記録
  useEffect(() => {
    const map = processingStartRef.current;
    for (const aj of activeJobs) {
      if (aj.status === 'polling' && !aj.waiting && map[aj.jobId] == null) {
        map[aj.jobId] = Date.now();
      }
    }
  }, [activeJobs]);

  // polling 中のジョブがある間だけ1秒ごとに再描画(経過時間・進捗の更新)
  useEffect(() => {
    if (!activeJobs.some(j => j.status === 'polling')) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [activeJobs]);


  // 新しいジョブが追加されたらリスト先頭にスムーズスクロール
  useEffect(() => {
    if (activeJobs.length > prevJobCountRef.current && listRef.current) {
      listRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    prevJobCountRef.current = activeJobs.length;
  }, [activeJobs.length]);

  const materialQuery = useQuery({
    queryKey: ['temporary-material', materialModalId],
    queryFn: async () => {
      const cachedMaterials = queryClient.getQueryData<{ items: TemporaryMaterial[] }>(['materials', 'temporary']);
      if (cachedMaterials?.items) {
        const found = cachedMaterials.items.find(m => m.id === materialModalId);
        if (found) return found;
      }
      return fetchTemporaryMaterial(materialModalId!);
    },
    enabled: !!materialModalId,
  });

  // 動画 ETA テーブル (実測ベースの所要時間予測)。動画ジョブがある間だけ取得し、
  // 1時間キャッシュ。失敗・未取得でも resolveExpectedSec が固定目安へフォールバックする。
  const hasVideoJob = activeJobs.some(j => j.type === 'video' && !!j.videoModel);
  const { data: etaTable } = useQuery({
    queryKey: ['video-eta-table'],
    queryFn: fetchVideoEtaTable,
    enabled: hasVideoJob,
    staleTime: 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
  });

  // 利用者の操作待ち (paused) の行は数えず・描画しない (ポーリング自体は続く)
  const visibleJobs = activeJobs.filter(j => !j.paused);
  const activeJobCount = visibleJobs.filter(j => j.status === 'polling').length;
  const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const pos = queueWindowPos ?? { x: window.innerWidth - 340, y: window.innerHeight - 400 };
    dragRef.current = { startX: clientX, startY: clientY, origX: pos.x, origY: pos.y };

    const handleMove = (ev: MouseEvent | TouchEvent) => {
      if (!dragRef.current) return;
      const cx = 'touches' in ev ? ev.touches[0].clientX : ev.clientX;
      const cy = 'touches' in ev ? ev.touches[0].clientY : ev.clientY;
      const dx = cx - dragRef.current.startX;
      const dy = cy - dragRef.current.startY;
      setQueueWindowPos({
        x: Math.max(0, Math.min(window.innerWidth - 100, dragRef.current.origX + dx)),
        y: Math.max(0, Math.min(window.innerHeight - 60, dragRef.current.origY + dy)),
      });
    };

    const handleEnd = () => {
      dragRef.current = null;
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
  }, [queueWindowPos]);

  if (!showJobQueueModal && !materialModalId) return null;

  return (
    <>
    {showJobQueueModal && (
    <div
      ref={queueWindowRef}
      className={`${styles.jobQueueWindow}${brandConfig.features.appChrome ? '' : ` ${styles.jobQueueWindowChromeless}`}`}
      style={queueWindowPos ? { left: queueWindowPos.x, top: queueWindowPos.y } : undefined}
    >
      <div
        className={styles.jobQueueDragHandle}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        <svg
          className={styles.jobQueueDragGrip}
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
        </svg>
        <h3 className={styles.jobQueueTitle}>生成キュー</h3>
        <span
          className={styles.jobQueueNotifyWrap}
          // ドラッグ開始と競合しないようにイベントを止める
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <NotifyToggleSwitch variant="panel" />
        </span>
        <button
          type="button"
          className={styles.jobQueueClose}
          onClick={() => setShowJobQueueModal(false)}
          aria-label="閉じる"
          title="閉じる"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>
      </div>
      <div ref={listRef} className={styles.jobQueueList}>
        {visibleJobs.length === 0 ? (
          <div className={styles.jobQueueEmpty}>キューは空です</div>
        ) : (
          [...visibleJobs].reverse().map((aj) => {
            const baseline = processingStartRef.current[aj.jobId] ?? aj.startedAt;
            const expectedSec = resolveExpectedSec(aj, etaTable);
            // バックエンドの実進捗があればそれを優先し、無ければ経過時間ベースの疑似進捗
            const progress = typeof aj.progress === 'number'
              ? Math.max(0, Math.min(99, aj.progress))
              : pseudoProgress(now - baseline, expectedSec);
            return (
              <QueueItem
                key={aj.jobId}
                aj={aj}
                now={now}
                progress={progress}
                baseline={baseline}
                expectedSec={expectedSec}
                onOpenMaterial={setMaterialModalId}
                onDismiss={(jobId) => {
                  setActiveJobs(prev => prev.filter(j => j.jobId !== jobId));
                }}
              />
            );
          })
        )}
      </div>
      {activeJobs.some(j => j.status !== 'polling') && (
        <div className={styles.jobQueueFooter}>
          <button
            type="button"
            className={styles.jobQueueClearBtn}
            onClick={() => {
              setActiveJobs(prev => prev.filter(j => j.status === 'polling'));
              if (activeJobCount === 0) setShowJobQueueModal(false);
            }}
          >
            完了済みをクリア
          </button>
        </div>
      )}
    </div>
    )}
    {materialModalId && materialQuery.data && (
      <MaterialPreviewModal
        material={materialQuery.data}
        materialId={materialModalId}
        onClose={() => setMaterialModalId(null)}
        showEditControls={true}
        showAddToSelectionButton={false}
        // 素材ボタン経由 (SimpleMaterialsList) と揃える。simpleMaterialsUi の
        // ブランド (usodeki) では生成完了サムネイルからもシェアできるようにする。
        showShareButton={brandConfig.features.simpleMaterialsUi ?? false}
        isTemporary={true}
        onRequestRefresh={async () => {
          queryClient.invalidateQueries({ queryKey: ['temporary-materials'] });
        }}
      />
    )}
    </>
  );
}

// 動画ジョブの「残り 約N分」表示。予測尺と経過時間から残りを概算する。
// ばらつきが大きいため分単位の概数で出し、超過後は「まもなく完了」に切り替える。
function formatRemaining(baseline: number, now: number, expectedSec: number): string | null {
  const remainSec = expectedSec - (now - baseline) / 1000;
  if (remainSec <= 15) return 'まもなく完了';
  if (remainSec < 90) return `残り 約${Math.ceil(remainSec / 10) * 10}秒`;
  return `残り 約${Math.ceil(remainSec / 60)}分`;
}

function QueueItem({ aj, now, progress, baseline, expectedSec, onOpenMaterial, onDismiss }: { aj: ActiveJob; now: number; progress: number; baseline: number; expectedSec: number; onOpenMaterial: (id: string) => void; onDismiss: (jobId: string) => void }) {
  const thumbnailUrl = aj.status === 'completed' ? aj.result?.image_url : undefined;
  const studioUrl = aj.type === 'youtube-upload' ? (aj.result?.studio_url as string | undefined) : undefined;
  const videoEditTexts = aj.type ? VIDEO_EDIT_JOB_TEXTS[aj.type] : undefined;
  // MP4 書き出しの完了項目: 旧「書き出し状況」ポップオーバーの中身 (ファイル名・解像度・サイズ・
  // ラウドネス注記) をここに出す。行自体はクリックできず、ラベル横の緑のダウンロードアイコンだけが
  // 再ダウンロードのボタン (他の完了項目のように行全体をプレビューの入口にしない)。
  const renderResult = videoEditRenderResultOf(aj);
  const [redownloading, setRedownloading] = useState(false);
  const [redownloadError, setRedownloadError] = useState<string | null>(null);
  const isClickable = aj.status === 'completed' && !renderResult && (!!aj.result?.material_id || !!studioUrl);
  // ✕ の確認 (行内の二段クリック)。polling 中の行にだけ出す。
  // シナリオ生成だけは「作業エリアから切り離す」意味 (docs/features/scenario-generation-detach-plan.md §4.8)。
  const isScenario = aj.type === 'scenario';
  const [confirming, setConfirming] = useState(false);
  // モーションのこだわり生成の候補と中断 (§10.10.1)。motion_stop が状態 API から返るまではローカルの受付で表示する
  const toast = useToast();
  const candidates = aj.status === 'polling' ? (aj.candidates ?? []) : [];
  const [localStop, setLocalStop] = useState<Pick<MotionStop, 'pick'> | null>(null);
  const [stopping, setStopping] = useState(false);
  const effectiveStop = aj.motionStop ?? localStop;
  const stageText = effectiveStop ? motionStopStageText(effectiveStop) : aj.stage;
  const handleMotionStop = async (pick?: number) => {
    if (stopping || effectiveStop) return;
    setStopping(true);
    try {
      await stopMotionJob(aj.jobId, pick);
      setLocalStop(pick != null ? { pick } : {});
    } catch (err) {
      toast.error(motionStopErrorMessage(err));
    } finally {
      setStopping(false);
    }
  };
  // 確認を出している間に生成が終わったら (polling でなくなったら) 確認を畳む。
  useEffect(() => {
    if (aj.status !== 'polling') setConfirming(false);
  }, [aj.status]);

  // MP4 書き出し: 書き出し時と同じファイル名で再ダウンロード (自動ダウンロードを取りこぼした /
  // 取得に失敗したときの再試行)。エディタを離れていても素材から取り直せる。
  const handleRedownload = async () => {
    if (!renderResult || redownloading) return;
    setRedownloading(true);
    setRedownloadError(null);
    try {
      const blob = await fetchMaterialContentBlob(renderResult.material_id);
      downloadBlob(blob, renderResult.filename);
    } catch {
      setRedownloadError('書き出した動画の取得に失敗しました');
    } finally {
      setRedownloading(false);
    }
  };

  const handleClick = async () => {
    if (!isClickable) return;

    // YouTube アップロード完了: YouTube Studio を開く
    if (aj.type === 'youtube-upload') {
      if (studioUrl) window.open(studioUrl, '_blank', 'noopener');
      return;
    }

    if (!aj.result?.material_id) return;

    // 連結動画はfetch→Blobでダウンロード強制
    if (aj.type === 'concat-video') {
      try {
        // 署名付きURL直fetch → API中継の順で取得する (CORS未設定バケット対策)
        const blob = await fetchMaterialContentBlob(aj.result.material_id as string);
        downloadBlob(blob, previewFilename(aj.label || 'preview'));
      } catch {
        // フォールバック: プレビューモーダルを開く
        onOpenMaterial(aj.result.material_id as string);
      }
      return;
    }

    onOpenMaterial(aj.result.material_id as string);
  };

  return (
    <>
    <div
      className={`${styles.jobQueueItem} ${isClickable ? styles.jobQueueItemClickable : ''}`}
      onClick={handleClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(); } : undefined}
    >
      {/* サムネイル or ステータスアイコン */}
      <div className={styles.jobQueueItemStatus}>
        {aj.status === 'polling' && <span className={styles.jobQueueItemSpinner} />}
        {aj.status === 'completed' && (
          thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt="生成結果"
              className={styles.jobQueueItemThumb}
            />
          ) : (
            <span className={styles.jobQueueItemCheck}>✓</span>
          )
        )}
        {aj.status === 'failed' && <span className={styles.jobQueueItemError}>✕</span>}
      </div>
      <div className={styles.jobQueueItemInfo}>
        {aj.label && (
          <span className={styles.jobQueueItemSceneLabel}>{aj.label}</span>
        )}
        {/* 実行中の段階 (backend の stage 通知)。モーショングラフィックス等の長いジョブで「いま何をしているか」を出す */}
        {!videoEditTexts && aj.status === 'polling' && !aj.waiting && stageText && (
          <span className={styles.jobQueueItemStage}>{stageText}</span>
        )}
        <span className={styles.jobQueueItemLabel}>
          {videoEditTexts && aj.status === 'polling' && (aj.waiting ? '順番待ち中...' : videoEditTexts.running)}
          {videoEditTexts && aj.status === 'completed' && videoEditTexts.completed}
          {videoEditTexts && aj.status === 'failed' && videoEditTexts.failed}
          {!videoEditTexts && aj.status === 'polling' && (
            aj.waiting
              ? '順番待ち中...'
              : aj.type === 'video'
                ? '動画生成中...'
                : aj.type === 'scene-preview'
                  ? 'シーン画像生成中...'
                  : aj.type === 'concat-video'
                    ? 'プレビュー動画生成中...'
                    : aj.type === 'storyboard'
                      ? 'ストーリーボードを生成中...'
                      : aj.type === 'scenario'
                        ? 'シナリオを生成中...'
                        : aj.type === 'youtube-upload'
                          ? `${aj.stage || 'YouTubeにアップロード'}...`
                          : aj.type === 'voice'
                            ? 'ナレーションを生成中...'
                            : '画像生成中...'
          )}
          {!videoEditTexts && aj.status === 'completed' && (
            aj.type === 'video'
              ? '動画生成完了'
              : aj.type === 'scene-preview'
                ? 'シーン画像生成完了'
                : aj.type === 'concat-video'
                  ? 'プレビュー動画生成完了'
                  : aj.type === 'storyboard'
                    ? 'ストーリーボード生成完了'
                    : aj.type === 'scenario'
                      ? 'シナリオ生成完了'
                      : aj.type === 'youtube-upload'
                        ? 'YouTubeアップロード完了'
                        : aj.type === 'voice'
                          ? 'ナレーションの生成完了'
                          : '画像生成完了'
          )}
          {!videoEditTexts && aj.status === 'failed' && (
            aj.type === 'video'
              ? '動画生成エラー'
              : aj.type === 'scene-preview'
                ? 'シーン画像生成エラー'
                : aj.type === 'concat-video'
                  ? 'プレビュー動画生成エラー'
                  : aj.type === 'storyboard'
                    ? 'ストーリーボード生成エラー'
                    : aj.type === 'scenario'
                      ? 'シナリオ生成エラー'
                      : aj.type === 'youtube-upload'
                        ? 'YouTubeアップロードエラー'
                        : aj.type === 'voice'
                          ? 'ナレーションの生成エラー'
                          : '画像生成エラー'
          )}
          {/* 完了ラベル末尾の緑のダウンロードボタン。これだけが再ダウンロードの入口 (行はクリック不可) */}
          {renderResult && (
            <button
              type="button"
              className={styles.jobQueueItemDownloadBtn}
              onClick={() => void handleRedownload()}
              disabled={redownloading}
              title="もう一度ダウンロード"
              aria-label="もう一度ダウンロード"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </button>
          )}
          {aj.status === 'completed' && aj.type === 'youtube-upload' && studioUrl && (
            <svg
              className={styles.jobQueueItemExtLinkIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          )}
        </span>
        {aj.status === 'failed' && aj.error && (
          <span className={styles.jobQueueItemErrorMsg}>{aj.error}</span>
        )}
        {renderResult && (
          <>
            <span className={styles.jobQueueItemMeta} title={renderResult.filename}>{renderResult.filename}</span>
            <span className={styles.jobQueueItemMeta}>
              {renderResult.width}x{renderResult.height} / {renderResult.fps}fps / {renderResult.durationSec.toFixed(2)}秒
              {renderResult.sizeBytes != null ? ` / ${formatBytes(renderResult.sizeBytes)}` : ''}
            </span>
            {renderResult.loudnessNote && (
              <span className={styles.jobQueueItemMeta}>{renderResult.loudnessNote}</span>
            )}
            {(redownloadError ?? renderResult.downloadError) && (
              <span className={styles.jobQueueItemErrorMsg}>{redownloadError ?? renderResult.downloadError}</span>
            )}
            {redownloading && <span className={styles.jobQueueItemMeta}>ダウンロードを準備しています...</span>}
          </>
        )}
        {aj.status === 'polling' && (
          <div className={styles.jobQueueItemProgress}>
            <div
              className={`${styles.jobQueueItemProgressBar} ${aj.waiting ? styles.jobQueueItemProgressWaiting : ''}`}
              style={aj.waiting ? undefined : { width: `${progress}%` }}
            />
          </div>
        )}
      </div>
      {aj.status === 'polling' && !aj.waiting ? (
        <span className={styles.jobQueueItemPercent}>
          {Math.round(progress)}%
          {aj.type === 'video' && aj.videoModel && (
            <span className={styles.jobQueueItemEta}>{formatRemaining(baseline, now, expectedSec)}</span>
          )}
        </span>
      ) : (
        <span className={styles.jobQueueItemTime}>{formatElapsed(aj.startedAt, now)}</span>
      )}
      {aj.status === 'polling' && !confirming && (
        <button
          type="button"
          className={styles.jobQueueItemDismiss}
          title={isScenario ? 'キューから外す' : 'キューから削除'}
          aria-label={isScenario ? 'キューから外す' : 'キューから削除'}
          onClick={(e) => {
            e.stopPropagation();
            // 確認は window.confirm ではなく行内の二段クリックで行う。ネイティブの confirm は
            // 埋め込みブラウザ (Claude デスクトップの内蔵ブラウザ等) で自動的にキャンセル扱いに
            // なり「押しても無反応」に見えるため。
            setConfirming(true);
          }}
        >
          ✕
        </button>
      )}
    </div>
    {candidates.length > 0 && (
      <MotionCandidateStrip
        candidates={candidates}
        stopped={!!effectiveStop}
        stopping={stopping}
        onStop={(pick) => void handleMotionStop(pick)}
      />
    )}
    {aj.status === 'polling' && confirming && (
      <div
        className={styles.jobQueueItemConfirm}
        role="group"
        aria-label={isScenario ? 'キューから外す確認' : 'キューから削除の確認'}
        onClick={(e) => e.stopPropagation()}
      >
        <span className={styles.jobQueueItemConfirmText}>
          {isScenario
            // シナリオ生成だけは「作業エリアから切り離す」意味になる (計画書 §4.8)。
            // 走っている生成は止められないが、外すと即座に次の生成を投入できる。
            ? '生成は裏で続き、完了後にシナリオブックへ保存されます（完了の通知は出ません）。ポイントは返還されず、絵コンテ・ナレーションの自動生成は行いません。'
            : '開始した生成処理は停止できないため、ポイントも返還されません。'}
        </span>
        <span className={styles.jobQueueItemConfirmActions}>
          <button
            type="button"
            className={styles.jobQueueItemConfirmCancel}
            onClick={() => setConfirming(false)}
          >
            取消
          </button>
          <button
            type="button"
            className={styles.jobQueueItemConfirmOk}
            onClick={() => {
              setConfirming(false);
              // track が見つからない (リロード等で失われた行) 場合も、行はそのまま消す。
              if (isScenario) detachTrack(aj.jobId);
              onDismiss(aj.jobId);
            }}
          >
            {isScenario ? 'キューから外す' : '削除する'}
          </button>
        </span>
      </div>
    )}
    </>
  );
}
